import React, {ReactElement, useEffect} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import {useGetStoreDetailsQuery} from '../../api/storesApi';
import {useLazyFetchUserWithout401RetryQuery} from '../../api/userApi';
import {useAppSelector, useStoreSlug} from '../../hooks';
import StoreClosed from '../../layouts/StoreLayout/StoreClosed';
import {AdminStoreRoles, AdminRoles} from '../../models/user';
import './index.scss';

interface StoreClosedWrapperProps {
  children: React.ReactNode;
}

const Progress: React.FC = () => (
  <Stack
    className="store-closed-wrapper"
    justifyContent="center"
    alignItems="center">
    <CircularProgress />
  </Stack>
);

const StoreClosedWrapper = ({
  children
}: StoreClosedWrapperProps): ReactElement => {
  const storeSlug = useStoreSlug();
  const {isSuccess: storeDetailsSuccess, isError: storeDetailsErrored} =
    useGetStoreDetailsQuery(storeSlug);
  const [
    triggerFetchUserQuery,
    {
      data: userDetailsData,
      isSuccess: userDetailsSuccess,
      isError: userDetailsErrored
    }
  ] = useLazyFetchUserWithout401RetryQuery();
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const isClosed = storeDetails?.status === 'closed';
  const isDraft = storeDetails?.status === 'draft';
  const isClosedOrDraft = isClosed || isDraft;
  const isOpen = storeDetails?.status === 'open';

  useEffect(() => {
    if (isClosedOrDraft) triggerFetchUserQuery({storeSlug});
  }, [isClosedOrDraft]);

  const isAdmin = Object.values(AdminRoles).includes(
    userDetailsData?.role as AdminRoles
  );
  const isAuthorized =
    userDetailsData &&
    userDetailsData.storeRoles?.some(role =>
      Object.values(AdminStoreRoles).includes(role as AdminStoreRoles)
    );
  const storeDetailsFinished = storeDetailsSuccess || storeDetailsErrored;
  const userDetailsFinished = userDetailsSuccess || userDetailsErrored;
  const requestsFinished =
    storeDetailsFinished && (isOpen || userDetailsFinished);

  if (isOpen) return <>{children}</>;

  if (!requestsFinished) return <Progress />;

  if (isClosedOrDraft && !isAdmin && !isAuthorized)
    return <StoreClosed storeSlug={storeSlug} />;

  return <>{children}</>;
};

export default StoreClosedWrapper;
